<script lang="ts" setup>
import { ElTooltip } from "element-plus";
import { computed, reactive, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  useGetVisitorFields,
  type VisitorFieldType,
} from "@/api/visitorFields";
import { useLocationSelector } from "@/composables/location";
import TextField from "@/components/common/CustomFields/TextField.vue";
import EmailField from "@/components/common/CustomFields/EmailField.vue";
import PhoneField from "@/components/common/CustomFields/PhoneField.vue";
import YesNoField from "@/components/common/CustomFields/YesNoField.vue";
import MultichoiceField from "@/components/common/CustomFields/MultichoiceField.vue";
import MultiselectField from "@/components/common/CustomFields/MultiselectField.vue";
import DateField from "@/components/common/CustomFields/DateField.vue";
import DocumentField from "@/components/common/CustomFields/DocumentField.vue";
import UnsupportedField from "@/components/common/CustomFields/UnsupportedField.vue";
import { VisitFieldTypes } from "@/api/zoolander";
import type { VisitorFieldValues } from "@/api/preregistrations";
import type { VisitorField } from "@/pages/settings/visitors/fields/types";

export interface Props {
  locationId: string;
  categoryId: string;
  fieldValues?: VisitorFieldValues;
  fields?: VisitorField[];
  enabledFields?: { id: string; label: string }[];
  isPreregistration?: boolean;
  required?: boolean;
  visitorName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isPreregistration: true,
});

const emit = defineEmits<{
  (e: "update", value: VisitorFieldValues): void;
}>();

const { t } = useI18n();

const { locationGroups } = useLocationSelector();

const locationGroup = computed(() =>
  locationGroups.value.find((group) =>
    group.locations.map(({ id }) => id).includes(props.locationId)
  )
);

const getVisitorFields = useGetVisitorFields({
  visitorCategoryId: toRef(props, "categoryId"),
  locationGroupId: locationGroup.value?.id,
});

const fieldValuesChangeable = reactive<VisitorFieldValues>({});

const visitorFields = computed(() => {
  if (!props.categoryId) {
    return props.fields || [];
  }

  if (props.enabledFields) {
    return getVisitorFields.data.value
      ?.filter(
        (field) =>
          props.enabledFields?.map(({ id }) => id).includes(field.id) ||
          field.active
      )
      .map((field) => ({
        ...field,
        title:
          props.enabledFields?.find(({ id }) => id === field.id)?.label ??
          field.title,
      }));
  }

  return getVisitorFields.data.value?.filter(({ active }) => active);
});

const visitorFieldComponent = (fieldType: VisitorFieldType) => {
  switch (fieldType) {
    case VisitFieldTypes.String:
      return TextField;
    case VisitFieldTypes.Email:
      return EmailField;
    case VisitFieldTypes.Phone:
      return PhoneField;
    case VisitFieldTypes.Yesno:
      return YesNoField;
    case VisitFieldTypes.Multichoice:
      return MultichoiceField;
    case VisitFieldTypes.Multiselect:
      return MultiselectField;
    case VisitFieldTypes.Dob:
      return DateField;
    case VisitFieldTypes.Document:
      return DocumentField;
    default:
      return UnsupportedField;
  }
};

watch(fieldValuesChangeable, (value) => emit("update", value), { deep: true });

watch(
  () => props.fieldValues,
  (value) => {
    if (!value) return;
    Object.entries(value).forEach(([fieldId, fieldValue]) => {
      fieldValuesChangeable[fieldId] = fieldValue;
    });
  },
  { deep: true, immediate: true }
);

const getFieldId = (field: VisitorField) => {
  return props.isPreregistration ? field.id : Number(field.numericId);
};
</script>

<template>
  <component
    :is="visitorFieldComponent(field.fieldType)"
    v-for="field in visitorFields"
    :id="field.id"
    :key="field.id"
    v-model="fieldValuesChangeable[getFieldId(field)]"
    :label="field.title"
    :options="field.fieldOptions"
    :disabled="!field.active"
    :settings="field.settings"
    :visitor-name="visitorName"
    :required="required && field.required"
    :is-preregistration="isPreregistration"
  >
    <template #label>
      {{ field.title }}
      <el-tooltip
        v-if="!field.active && categoryId"
        :content="t('visitors.fields.disabledField')"
        :show-after="500"
        placement="top"
      >
        <font-awesome-icon
          class="help-icon"
          :icon="['fal', 'exclamation-circle']"
        />
      </el-tooltip>
    </template>
  </component>
</template>

<style lang="css" scoped>
.help-icon {
  margin-left: 8px;
  font-size: 1.125rem;
  color: var(--so-text-color-regular);
  vertical-align: text-bottom;
}
</style>
